<template>
  <div :class="rootClass" :style="rootStyle">
    <div class="d-flex align-center">
      <span class="app-field-label">{{ resolvedLabel }}</span>
    </div>
    <v-select
      v-bind="selectAttrs"
      :model-value="selectedIds"
      :items="items"
      item-title="display_name"
      item-value="id"
      multiple
      chips
      closable-chips
      clearable
      :label="undefined"
      :placeholder="$t('common.action.select')"
      persistent-placeholder
      :loading="isLoading"
      :disabled="disabled || !accountId"
      hide-details="auto"
      @update:model-value="handleUpdate"
    >
      <template #prepend-item>
        <v-list-item :title="allLocationsTitle" @click="toggleAllLocations">
          <template #prepend>
            <v-checkbox-btn
              :indeterminate="likesSomeLocation && !likesAllLocations"
              :model-value="likesAllLocations"
            />
          </template>
        </v-list-item>
        <v-divider class="mt-2" />
      </template>

      <template #selection="{ index }">
        <v-chip
          v-if="!likesAllLocations || index === 0"
          closable
          @click:close.stop="removeSelection(selectedIds[index])"
        >
          {{ likesAllLocations ? allLocationsTitle : getLocationTitle(selectedIds[index]) }}
        </v-chip>
      </template>
    </v-select>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, useAttrs, watch, type CSSProperties } from "vue";
import { collection, onSnapshot, query, where, type Unsubscribe } from "firebase/firestore";
import { db } from "@/app/plugins/firebase";
import { useAccountStore } from "@/account/stores/account";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import i18n from "@/app/plugins/i18n";
import {
  filterLocationsByAllowedLocationIds,
  normalizeLocationIds,
  type LocationOption,
} from "@/terminals/utils/location.utils";

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    modelValue: string[] | null;
    label?: string;
    disabled?: boolean;
    allLocationsLabel?: string;
    allowedLocationIds?: string[] | null;
    collapseAllSelection?: boolean;
  }>(),
  {
    modelValue: null,
    label: undefined,
    disabled: false,
    allLocationsLabel: undefined,
    allowedLocationIds: undefined,
    collapseAllSelection: true,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string[] | null): void;
}>();

const accountStore = useAccountStore();
const accountId = computed(() => accountStore.account?.id ?? null);
const isLoading = ref(false);
const locations = ref<LocationOption[]>([]);
const unsubscribe = ref<Unsubscribe | null>(null);

const allowedLocationIdsNormalized = computed(() => normalizeLocationIds(props.allowedLocationIds));

const visibleLocations = computed<LocationOption[]>(() =>
  filterLocationsByAllowedLocationIds(locations.value, allowedLocationIdsNormalized.value),
);

const allLocationsTitle = computed(() => {
  if (props.allLocationsLabel) {
    return props.allLocationsLabel;
  }
  const translated = i18n.global.t("common.label.allLocations");
  return translated === "common.label.allLocations" ? "All locations" : translated;
});

const resolvedLabel = computed(() => {
  if (props.label) {
    return props.label;
  }
  const translated = i18n.global.t("page.locations.locations");
  return translated === "page.locations.locations" ? "Locations" : translated;
});

const attrs = useAttrs();
type ClassBinding = string | string[] | Record<string, boolean>;
type StyleBinding = string | CSSProperties | Array<string | CSSProperties>;

const rootClass = computed<ClassBinding | undefined>(() => {
  const value = attrs.class;
  if (
    typeof value === "string" ||
    Array.isArray(value) ||
    (value !== null && typeof value === "object")
  ) {
    return value as ClassBinding;
  }
  return undefined;
});

const rootStyle = computed<StyleBinding | undefined>(() => {
  const value = attrs.style;
  if (
    typeof value === "string" ||
    Array.isArray(value) ||
    (value !== null && typeof value === "object")
  ) {
    return value as StyleBinding;
  }
  return undefined;
});

const selectAttrs = computed(() => {
  const attrsObject = { ...attrs } as Record<string, unknown>;
  delete attrsObject.class;
  delete attrsObject.style;
  return attrsObject;
});

const items = computed<LocationOption[]>(() =>
  [...visibleLocations.value].sort((a, b) => a.display_name.localeCompare(b.display_name)),
);

const availableLocationIds = computed(() => visibleLocations.value.map((location) => location.id));

const selectedIds = ref<string[]>([]);

const likesAllLocations = computed(
  () =>
    availableLocationIds.value.length > 0 &&
    selectedIds.value.length === availableLocationIds.value.length,
);
const likesSomeLocation = computed(() => selectedIds.value.length > 0);

const syncFromModelValue = () => {
  const validIds = availableLocationIds.value;
  selectedIds.value =
    props.modelValue === null
      ? [...validIds]
      : props.modelValue.filter((id) => validIds.includes(id));
};

const handleUpdate = (next: string[]) => {
  const validIds = availableLocationIds.value;
  const filtered = next.filter((id) => validIds.includes(id));

  selectedIds.value = filtered;
  emit(
    "update:modelValue",
    props.collapseAllSelection && validIds.length > 0 && filtered.length === validIds.length
      ? null
      : filtered,
  );
};

const toggleAllLocations = () => {
  handleUpdate(likesAllLocations.value ? [] : [...availableLocationIds.value]);
};

const removeSelection = (locationId: string) => {
  if (likesAllLocations.value) {
    handleUpdate([]);
    return;
  }
  handleUpdate(selectedIds.value.filter((id) => id !== locationId));
};

const getLocationTitle = (locationId: string) =>
  items.value.find((location) => location.id === locationId)?.display_name ?? locationId;

const stopListener = () => {
  if (unsubscribe.value) {
    unsubscribe.value();
    unsubscribe.value = null;
  }
};

const startListener = (activeAccountId: string) => {
  stopListener();
  isLoading.value = true;

  const locationsRef = collection(db, "accounts", activeAccountId, "locations");
  const firebaseQuery = query(locationsRef, where("deleted", "==", false));

  unsubscribe.value = onSnapshot(
    firebaseQuery,
    (snapshot) => {
      locations.value = snapshot.docs.map((doc) => {
        const data = doc.data() as { display_name?: string; name?: string };
        return {
          id: doc.id,
          display_name: data?.display_name ?? data?.name ?? doc.id,
        };
      });
      isLoading.value = false;
    },
    (error) => {
      isLoading.value = false;
      console.error("Failed to load locations from Firestore", error);
      useSnackBarStore().toastError({
        message: error?.message || "Failed to load locations",
      });
      stopListener();
    },
  );
};

watch(
  accountId,
  (newAccountId) => {
    locations.value = [];
    if (!newAccountId) {
      stopListener();
      return;
    }
    startListener(newAccountId);
  },
  { immediate: true },
);

watch([availableLocationIds, () => props.modelValue], () => syncFromModelValue(), {
  immediate: true,
});

onBeforeUnmount(() => {
  stopListener();
});
</script>
